/**
 * The one place the frontend speaks HTTP to the FastAPI backend.
 *
 * The backend serves this bundle, so its API is same-origin: a path like
 * `/api/v1/reports` is the whole URL. There is no base URL to configure, which
 * is what makes one build correct in every environment.
 *
 * Authentication is a cookie, not a header. The backend sets an httpOnly,
 * `SameSite=Strict`, host-only session cookie at sign-in; `credentials:
 * "include"` is what sends it back. No access token is ever readable by
 * JavaScript here, so there is no token to forward, store or leak.
 *
 * Failure is a value, not an exception. A page that cannot reach the backend
 * should render the rest of itself and say what is unavailable, rather than
 * collapsing into an error boundary that tells the analyst nothing.
 */

/** Long enough for a cold Render instance, short enough that a page still renders. */
const TIMEOUT_MS = 8_000;

export type ApiFailure =
  | { kind: "unauthenticated"; detail: string }
  | { kind: "unreachable"; detail: string }
  | { kind: "error"; status: number; detail: string };

export type ApiResult<T> = { ok: true; data: T } | { ok: false; failure: ApiFailure };

/** Human-readable reason, for the banner the analyst actually sees. */
export function describeFailure(failure: ApiFailure): string {
  switch (failure.kind) {
    case "unauthenticated":
      return `Showing sample data — the backend rejected this session: ${failure.detail}`;
    case "unreachable":
      return `Showing sample data — the backend could not be reached: ${failure.detail}`;
    case "error":
      return `Showing sample data — the backend returned ${failure.status}: ${failure.detail}`;
  }
}

interface ApiErrorBody {
  detail?: unknown;
  message?: unknown;
}

/** FastAPI puts the message under `detail`; our handlers sometimes use `message`. */
function extractDetail(body: unknown, fallback: string): string {
  if (typeof body === "object" && body !== null) {
    const { detail, message } = body as ApiErrorBody;
    if (typeof detail === "string" && detail) return detail;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
}

async function toFailure(response: Response): Promise<ApiFailure> {
  const body: unknown = await response.json().catch(() => null);
  if (response.status === 401 || response.status === 403) {
    return { kind: "unauthenticated", detail: extractDetail(body, response.statusText) };
  }
  return {
    kind: "error",
    status: response.status,
    detail: extractDetail(body, response.statusText),
  };
}

async function request<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  try {
    const response = await fetch(path, {
      method,
      credentials: "include",
      headers: body === undefined
        ? { Accept: "application/json" }
        : { Accept: "application/json", "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(TIMEOUT_MS),
      // Per-user, authorization-dependent data must never be cached across users.
      cache: "no-store",
    });

    if (!response.ok) return { ok: false, failure: await toFailure(response) };

    // 204 and an empty body are successes with nothing to parse.
    if (response.status === 204) return { ok: true, data: undefined as T };
    const text = await response.text();
    return { ok: true, data: (text ? JSON.parse(text) : undefined) as T };
  } catch (cause) {
    // A timeout arrives as an AbortError; a refused connection as a TypeError.
    const detail = cause instanceof Error ? cause.message : String(cause);
    return { ok: false, failure: { kind: "unreachable", detail } };
  }
}

export function apiGet<T>(path: string): Promise<ApiResult<T>> {
  return request<T>("GET", path);
}

export function apiPost<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
  return request<T>("POST", path, body ?? {});
}

export function apiPatch<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>("PATCH", path, body);
}

export function apiDelete<T>(path: string): Promise<ApiResult<T>> {
  return request<T>("DELETE", path);
}
